import { ColorType, CrosshairMode, PriceScaleMode } from 'lightweight-charts'
import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { chartLayoutOptions, ChartViewKey, subscriptionTags } from '../../../assets/constants'
import { useDispatch, useSelector } from 'react-redux'
import { Button, useTheme } from '@mui/material'
import './chartWrapper.css'
import { AppContext } from '../../../App'
import { selectTimezone } from '../../../selectors/common'
import { selectSnapshot } from '../../../selectors/security'
import { WebSocketContext, WebSocketContextValue } from '../../webSocketConnection/WebSocket'
import websocketActions from '../../../actions/websocket.actions'
import Chart from '../chart'
import { selectPrimarySecurity } from '../../../selectors/home'
import {
    selectMultiStrikeAtm,
    selectMultiStrikeChartCallPut,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeChartTimebucket,
    selectMultiStrikeChartType,
    selectMultiStrikeCumOi,
    selectMultiStrikeKey,
    selectMultiStrikeLoading,
    selectMultiStrikeNearAtm,
    selectMultiStrikeShowChange
} from '../../../selectors/multiStrikeChart'
import { ChartMetadata } from './chartMetadata/chartMetadata'
import { exchangeFromJSON } from '../../../proto/generated/common'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../selectors/options'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { callNative } from '../../../util/nativeBridge'
import { getStrikeAndStrikeData } from '../../../pages/home/openInterestHeader/MultiStrikesMenu/MultiStrikesMenu.tsx'
import { MultiStrikeSecuritiesType, setMultiStrikeChartKey, setMultiStrikeChartLoading, setMultiStrikeSecurities } from '../../../reducer/multiStrikeChart.reducer'
import { SearchParamsContext } from '../../providers/searchParamsProvider'
import {
    selectChartTimeBucket,
    selectMultiStrikeChartConfigAtm,
    selectMultiStrikeChartConfigCallPut,
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigCumOi,
    selectMultiStrikeChartConfigExpiry,
    selectMultiStrikeChartConfigNearAtm,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigShowChange,
    selectMultiStrikeChartConfigStyles,
    selectMultiStrikeConfigChartKey,
    selectTimeBuckets,
    selectTradingCalendarDataByExchange
} from '../../../selectors/chart'
import { formatLargeNumber } from '../../../util/screener'
import { areTwoRangesAlmostSame, CrossHairInfo, DataUpdateRequestType, ViewportCandleInfo } from '../../../util/chart'
import ChartSettingsButtons from '../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import { setChartLoading, updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { ChartSnapshotMultiStrikeChart } from '../chart.typings'
import { primarySecColorDarkTheme, primarySecColorLightTheme } from '../styles'
import { findNearestNumber } from '../../../util/tradepanel'
import { NonInteractiveTooltip } from '../../custom'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import ConfigurationPopup from './chartMetadata/configPopup/ConfigurationPopup'
import MultiStrikeSettingsDialog from '../../widgetDashboard/widgets/multiStrikeWidget/multiStrikeSettingsDialog'
import { debounce } from 'throttle-debounce'
import { selectIsEmbedded } from '../../../selectors/device.ts'

export type ChartContextType = {
    hoverActive: boolean
    // chart: Chart,
    viewKey: string
    displayChangeToggle?: boolean
    displayCumulativeToggle?: boolean
}

const ChartWrapper = forwardRef<any, any>((props, ref) => {
    const { viewKey, isWidget = false, securityList, onCrosshairMove, onTimeRangeChange, persistViewport = true, allowSplit, displayChangeToggle = true, displayCumulativeToggle = true } = props
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const appContext = useContext(AppContext)
    const memoizedViewKey = useMemo(() => viewKey, [viewKey])
    const [container, setContainer] = useState<HTMLDivElement>()
    const memoizedSecurityList = useMemo(() => securityList, [securityList])
    const handleRef = useCallback((ref: HTMLDivElement) => {
        setContainer(ref)
    }, [])
    const { key } = useSelector(selectPrimarySecurity)
    const secKey = useMemo(() => {
        if (isWidget) {
            return memoizedSecurityList[0]
        }
        return key
    }, [isWidget, key, memoizedSecurityList])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (secKey) getSecurityAsync(secKey).then(setSecurity); else setSecurity(undefined) }, [secKey])
    const multiStrikeChartSecurities = useSelector(isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities)
    const callPut = useSelector(isWidget ? selectMultiStrikeChartConfigCallPut(viewKey) : selectMultiStrikeChartCallPut)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const chartStyles = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const multiStikeChartKey = useSelector(isWidget ? selectMultiStrikeConfigChartKey(viewKey) : selectMultiStrikeKey)
    const underlyingToken = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const underlyingSnaphot = useSelector(selectSnapshot(underlyingToken))
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken))
    const { searchParams, setSearchParams } = useContext(SearchParamsContext)!
    const nearAtm = useSelector(isWidget ? selectMultiStrikeChartConfigNearAtm(viewKey) : selectMultiStrikeNearAtm)
    const atm = useSelector(isWidget ? selectMultiStrikeChartConfigAtm(viewKey) : selectMultiStrikeAtm)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const configExpiry = useSelector(selectMultiStrikeChartConfigExpiry(viewKey))
    const [openSettings, setOpenSettings] = useState(false)
    const expiry = useMemo(() => {
        if (isWidget) {
            return configExpiry
        }
        return decodeURIComponent(searchParams.get('expiry') || '')
    }, [configExpiry, isWidget, searchParams?.get('expiry'), searchParams.toString()])
    const expiryObj = useMemo(() => {
        return optionsExpiries?.find((o) => o.expiry.name === expiry)?.expiry
    }, [expiry, optionsExpiries])
    const isEmbedded = useSelector(selectIsEmbedded)
    const timeBucket = useSelector(isWidget ? selectChartTimeBucket(viewKey) : selectMultiStrikeChartTimebucket)
    const timeBuckets = useSelector(selectTimeBuckets)
    const timezone = useSelector(selectTimezone)
    const chart = useRef<Chart>(undefined)
    const [chartSnapshot, setChartSnapshot] = useState<ChartSnapshotMultiStrikeChart>(new Map())
    const [hoveredTime, setHoveredTime] = useState<number>(0)
    const calendarData = useSelector(selectTradingCalendarDataByExchange(security?.exchange))
    const loading = useSelector(selectMultiStrikeLoading)
    const chartRef = useRef<HTMLDivElement>(null)
    const firstTimeDataLoaded = useRef(false)
    const prevViewportCandleInfoRef = useRef<ViewportCandleInfo | null>(null)
    const multiStrikeChartRef = useMemo(() => {
        if (optionsExpiries?.length && expiry && multiStrikeChartSecurities) {
            return Object.fromEntries(
                Object.entries(multiStrikeChartSecurities!!).map(([strike, callPutArray]) => {
                    const finalExpiry = optionsExpiries?.find((o) => o.expiry.name === expiry)
                    const isDecimal = finalExpiry?.strikes.some((s) => !Number.isInteger(s.strike / (security?.priceMultiplier ?? 100)))
                    const strikes = finalExpiry?.strikes.find((st) => (isDecimal ? (st.strike / 100).toFixed(2) : JSON.stringify(st.strike / 100)) === strike)
                    return [
                        strike,
                        {
                            callSecKey: strikes?.callSecurity ? getSecurityKey(strikes.callSecurity.exchange, strikes.callSecurity.code) : '',
                            putSecKey: strikes?.putSecurity ? getSecurityKey(strikes.putSecurity.exchange, strikes.putSecurity.code) : '',
                            callPut: callPutArray
                        }
                    ]
                })
            )
        }
        return
    }, [expiry, multiStrikeChartSecurities, optionsExpiries])

    const sendConfigToNative = useCallback(debounce(500, (config: string) => {
        callNative('onMultistrikeConfigChange', config)
    }), [])

    useEffect(() => {
        if (isEmbedded) {
            const config = {
                key: secKey,
                expiry: expiryObj,
                securities: multiStrikeChartSecurities,
                chartType,
                cumOi,
                nearAtm,
                atm,
                showChange,
                timeBucket
            }
            sendConfigToNative(JSON.stringify(config))
        }
    }, [sendConfigToNative, isEmbedded, secKey, expiryObj, multiStrikeChartSecurities, chartType, cumOi, nearAtm, atm, showChange, timeBucket])

    const timeRangeChangeListener = useCallback(
        (viewportCandleInfo: ViewportCandleInfo) => {
            if (!prevViewportCandleInfoRef.current || !areTwoRangesAlmostSame(prevViewportCandleInfoRef.current, viewportCandleInfo, chart.current?.chartApi.timeScale()!!)) {
                onTimeRangeChange && onTimeRangeChange(viewportCandleInfo, chart.current?.notifyGoToDateToChart ? chart.current?.gotoDate : undefined)
                prevViewportCandleInfoRef.current = viewportCandleInfo
            }
        },
        [onTimeRangeChange]
    )

    const handleLoading = useCallback(
        (loadingValue: boolean) => {
            if (isWidget) {
                dispatch(setChartLoading({ viewKey, loading: loadingValue }))
            } else {
                dispatch(setMultiStrikeChartLoading(loadingValue))
            }
        },
        [dispatch, isWidget, viewKey]
    )

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTimeRangeChangeListener(timeRangeChangeListener)
        }
    }, [timeRangeChangeListener])

    useEffect(() => {
        if (secKey !== multiStikeChartKey) {
            if (isWidget) {
                dispatch(
                    updateMultiStrikeChartConfig({
                        viewKey,
                        config: {
                            key: secKey,
                            securities: {}
                        }
                    })
                )
            } else {
                dispatch(setMultiStrikeSecurities({}))
                dispatch(setMultiStrikeChartKey(secKey))
            }
        }
    }, [dispatch, secKey, multiStikeChartKey])

    useEffect(() => {
        ;(async () => {
            if (chart.current && secKey) {
                chart.current.dataManager.removeAllSecondarySecurities(true)
                chart.current.primary = secKey
                await chart.current.fetchData(true, true)
                firstTimeDataLoaded.current = false
            }
        })()
    }, [secKey])

    useEffect(() => {
        if (chart.current) {
            chart.current.callPutSelected = callPut ?? ['CE', 'PE']
            chart.current.updateCumOiCallPutVisible()
        }
    }, [callPut])

    useEffect(() => {
        if (chart.current) {
            chart.current.showChange = showChange
            chart.current.onDataLoaded(DataUpdateRequestType.HISTORICAL, false)
        }
    }, [showChange])

    useEffect(() => {
        const secondarySecurities = Object.keys(multiStrikeChartRef ?? {})
            .map((strike) => {
                const sec = multiStrikeChartRef?.[strike]
                const keys = [sec?.callSecKey, sec?.putSecKey].filter((k): k is string => Boolean(k))
                return keys.map((key) => ({
                    exchange: exchangeFromJSON(key.split(':')[0]),
                    code: key.split(':')[1]
                }))
            })
            .flat()
        const list = [
            {
                exchange: exchangeFromJSON(secKey.split(':')[0]),
                code: secKey.split(':')[1]
            },
            ...secondarySecurities
        ]
        websocketActions.setWebsocketSubscribeList(dispatch, {
            candleSubscriptions: [
                {
                    tag: subscriptionTags.MULTI_STRIKES_CHART,
                    securities: list,
                    timeBucket: { timeSeries: 1, value: 1 }
                }
            ]
        })
    }, [multiStrikeChartRef, secKey, dispatch])

    useEffect(() => {
        ;(async () => {
            if (chart.current) {
                chart.current.timeBucket = timeBucket
                await chart.current.fetchData(true, true)
            }
        })()
    }, [timeBucket])

    useEffect(() => {
        const currentExpiryIndex = optionsExpiries?.filter((o) => !o.derived).findIndex((o) => o.expiry.name === expiry)
        if (optionsExpiries && optionsExpiries?.length > 0 && (currentExpiryIndex ?? -1) < 0) {
            const tempExpiry = optionsExpiries?.filter((o) => !o.derived)[0].expiry.name
            if (tempExpiry) {
                if (isWidget) {
                    dispatch(updateMultiStrikeChartConfig({ viewKey, config: { expiry: tempExpiry } }))
                } else {
                    searchParams.set('expiry', tempExpiry)
                    setSearchParams(searchParams, { replace: true })
                }
            }
        }
    }, [secKey, optionsExpiries])

    const handleAtmStrikeClick = useCallback(
        async (i: number, isNearAtm: boolean, refresh = false) => {
            if (chart.current) {
                let optionExpiriesData = optionsExpiries
                let expiryIndex = refresh ? 0 : optionExpiriesData?.findIndex((o) => o.expiry.name === expiry)
                if (expiryIndex === -1 || !optionExpiriesData?.[expiryIndex as number]) {
                    expiryIndex = 0
                    const defaultExpiry = optionExpiriesData?.[expiryIndex]?.expiry?.name

                    if (defaultExpiry) {
                        dispatch(
                            updateMultiStrikeChartConfig({
                                viewKey,
                                config: {
                                    expiry: defaultExpiry,
                                    securities: {}
                                }
                            })
                        )
                    }
                }

                const expiryDataFinal = optionExpiriesData?.[expiryIndex as number]
                if (expiryDataFinal) {
                    const atmStrike = findNearestNumber(expiryDataFinal.strikesArray, underlyingSnaphot?.lastTradedPrice)
                    const atm = expiryDataFinal.strikes.find((s) => s.strike === atmStrike)
                    const isDecimal = expiryDataFinal.strikes.some((s) => !Number.isInteger(s.strike / (security?.priceMultiplier ?? 100)))
                    const atmIndex = expiryDataFinal.strikes.findIndex((s) => s.strike === atmStrike)

                    if (atm) {
                        const { strike, strikeData } = getStrikeAndStrikeData(atm, security?.priceMultiplier ?? 100, isDecimal)
                        let data = {
                            [strike]: strikeData
                        }
                        if (i > 0) {
                            const a = [3, 5, 7, 9]
                            a.forEach((x) => {
                                if (i >= x) {
                                    const index = x === 3 ? 1 : x === 5 ? 2 : x === 7 ? 3 : 4
                                    const atm1 = expiryDataFinal.strikes[atmIndex - index]
                                    const atm2 = expiryDataFinal.strikes[atmIndex + index]

                                    if (atm1 && atm2) {
                                        const { strike: strike1, strikeData: strikeData1 } = getStrikeAndStrikeData(atm1, security?.priceMultiplier ?? 100, isDecimal)
                                        const { strike: strike2, strikeData: strikeData2 } = getStrikeAndStrikeData(atm2, security?.priceMultiplier ?? 100, isDecimal)
                                        data = {
                                            [strike1]: strikeData1,
                                            ...data,
                                            [strike2]: strikeData2
                                        }
                                    }
                                }
                            })
                        } else {
                            data = {
                                ...multiStrikeChartRef
                            }
                        }
                        const formattedData: MultiStrikeSecuritiesType = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, value.callPut]))
                        if (isWidget) {
                            dispatch(
                                updateMultiStrikeChartConfig({
                                    viewKey,
                                    config: {
                                        ...(isNearAtm ? { nearAtm: i } : { securities: formattedData }),
                                        securities: formattedData,
                                        expiry: expiryDataFinal.expiry.name
                                    }
                                })
                            )
                        } else {
                            dispatch(setMultiStrikeSecurities(formattedData))
                        }
                        chart.current.dataManager.addNewStrikes(data)
                        chart.current.onCrosshairMove({} as any)
                    }
                }
            }
        },
        [optionsExpiries, expiry, underlyingSnaphot?.lastTradedPrice, security?.priceMultiplier, isWidget, dispatch, viewKey, multiStrikeChartRef]
    )

    useEffect(() => {
        if (!chart.current?.dataManager?.dataLoading && multiStrikeChartRef && Object.keys(multiStrikeChartRef).length === 0) {
            if (secKey === chart.current?.primary) {
                handleAtmStrikeClick(nearAtm ?? 3, nearAtm ? true : false)
            }
        }
    }, [expiry, optionsExpiries, chart?.current?.dataManager.dataLoading, handleAtmStrikeClick, nearAtm])

    const lastExpiry = useRef<string | null>(expiry ?? null)
    useEffect(() => {
        if (chart.current && !chart.current?.dataManager?.dataLoading && expiry && (!firstTimeDataLoaded.current || lastExpiry.current !== expiry) && optionsExpiries && optionsExpiries.length > 0) {
            if (nearAtm) {
                handleAtmStrikeClick(nearAtm, true)
            } else if (Object.keys(multiStrikeChartSecurities!!).length > 0) {
                handleAtmStrikeClick(-1, false)
            } else {
                handleAtmStrikeClick(3, false)
            }
            firstTimeDataLoaded.current = true
            lastExpiry.current = expiry ?? null
            chart.current.resetTimeScale()
        }
    }, [expiry, chart.current?.dataManager.dataLoading, handleAtmStrikeClick, nearAtm, multiStrikeChartSecurities, optionsExpiries])

    useEffect(() => {
        ;(async () => {
            if (chart.current && !chart.current?.dataManager?.dataLoading) {
                chart.current.resetTimeScale()
            }
        })()
    }, [expiry])

    const theme = useTheme()
    const layoutOptions = useMemo(() => {
        return {
            ...chartLayoutOptions,
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: theme.palette.text.primary
            },
            localization: {
                priceFormatter: (price: any) => {
                    return formatLargeNumber(price, 2)
                }
            },
            grid: {
                horzLines: {
                    color: '#88888815'
                },
                vertLines: {
                    color: '#88888815'
                }
            },
            timeScale: {
                borderColor: theme.palette.divider
            },
            rightPriceScale: {
                alignLabels: true,
                borderColor: theme.palette.divider,
                visible: true,
                mode: PriceScaleMode.Normal,
                borderVisible: false
            },
            leftPriceScale: {
                alignLabels: true,
                borderColor: theme.palette.divider,
                visible: true,
                mode: PriceScaleMode.Normal,
                borderVisible: false
            },
            crosshair: {
                mode: CrosshairMode.Normal
            }
        }
    }, [theme])

    useEffect(() => {
        const chart = appContext.getMultiStrikeChart(viewKey)

        if (chart) {
            chart.chartType = chartType
            chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false)
            chart.dataManager.fetchData({})
            chart.onCrosshairMove({} as any)
        }
    }, [viewKey, chartType])

    useEffect(() => {
        if (chart.current) {
            chart.current.theme = theme.palette.mode
            chart.current.dataManager.seriesApis[chart.current.primary]?.updateOptions({
                color: theme.palette.mode === 'dark' ? primarySecColorDarkTheme : primarySecColorLightTheme
            })
        }
    }, [theme, security])

    useEffect(() => {
        chart.current && chart.current.updateLayoutOptions(layoutOptions)
    }, [layoutOptions])

    useEffect(() => {
        if (chart.current) {
            chart.current.chartStyles = chartStyles
            chart.current.applyNewStyles()
        }
    }, [chartStyles])

    useEffect(() => {
        return () => {
            appContext.removeMultiStrikeChart(viewKey)
            const chartInstance = chart.current
            chart.current = undefined
            requestAnimationFrame(() => {
                chartInstance?.destroy()
            })
        }
    }, [appContext, viewKey])

    useEffect(() => {
        if (!container || chart.current) return

        chart.current = new Chart({
            id: 'chart-multi-strikes',
            container,
            layoutOptions,
            primary: secKey,
            tradingCalendarData: calendarData,
            secondarySecurities: multiStrikeChartRef ?? {},
            callPutSelected: callPut ?? ['CE', 'PE'],
            showChange: showChange,
            timeBucket,
            timeBuckets,
            dateRange: undefined,
            persistViewport: persistViewport || false,
            theme: theme.palette.mode,
            chartType,
            ws: ws as WebSocketContextValue,
            subscriptionTag: subscriptionTags.MULTI_STRIKES_CHART,
            chartStyles,
            emptyTimeslotsSize: 498,
            isEmbedded: isEmbedded,
            onDataUpdate() {
                if (!chart.current) return
                const snapshot = chart.current.getChartSnapshot()
                const time = chart.current.crossHairInfo?.time as number
                setHoveredTime(time)
                setChartSnapshot(snapshot)
            },
            onCrosshairMove(data?: CrossHairInfo) {
                data && onCrosshairMove && onCrosshairMove(data)
            },
            onTimeRangeChange: timeRangeChangeListener,
            onLoading: handleLoading,
            timezone
        })

        chart.current.chartApi.timeScale().applyOptions({
            timeVisible: true,
            secondsVisible: true
        })

        appContext.setMultiStrikeChart(viewKey, chart.current)
    }, [container, security])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateCrossHairMoveCallback((data?: CrossHairInfo) => {
                data && onCrosshairMove && onCrosshairMove(data)
            })
        }
    }, [onCrosshairMove])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTradingCalendarData(calendarData)
        }
    }, [calendarData])

    const chartContainerStyle: React.CSSProperties = useMemo(
        () => ({
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }),
        []
    )

    return (
        <div className={`chart-container font-feature-num rounded-none border-none ${loading ? 'blur-md' : ''}`} key={'chart-container'}>
            <div style={chartContainerStyle}>
                {isWidget && viewKey ? <ChartSettingsButtons viewKey={memoizedViewKey} allowSplit={allowSplit} security={security} /> : null}
                <div className="chart" ref={chartRef}>
                    <div ref={handleRef} id={`chart-${viewKey}`} style={{ cursor: 'crosshair' }} className="m-0 h-full w-full p-0" />
                    <NonInteractiveTooltip title="Settings" placement="top">
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={() => setOpenSettings(true)}
                            sx={{
                                ...{ right: '0px' },
                                position: 'absolute',
                                alignItems: 'center',
                                bottom: '0px',
                                width: 0
                            }}
                        >
                            <SettingsOutlinedIcon />
                        </Button>
                    </NonInteractiveTooltip>
                </div>
                {container && chartSnapshot ? (
                    <ChartMetadata
                        viewKey={viewKey}
                        container={container}
                        chartSnapshot={chartSnapshot}
                        hoverdTime={hoveredTime}
                        multiStrikeClassType={multiStrikeChartRef!!}
                        displayChangeToggle={displayChangeToggle}
                        displayCumulativeToggle={displayCumulativeToggle}
                    />
                ) : null}
                {openSettings && viewKey !== ChartViewKey.MULTI_STRIKES_CHART ? (
                    <MultiStrikeSettingsDialog open={openSettings} onClose={() => setOpenSettings(false)} viewKey={viewKey} />
                ) : (
                    openSettings && <ConfigurationPopup open={openSettings} onClose={() => setOpenSettings(false)} chartId={viewKey} />
                )}
            </div>
        </div>
    )
})

ChartWrapper.displayName = 'ChartWrapper'

export default ChartWrapper
